import { useImperativeHandle } from "react";
import { motion } from "framer-motion";
import { Flame, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import useSwipePhysics from "@/lib/use_swipe_physics";

// Every-fifth-card reminder for FREE renters: "you have N swipes left today"
// with the Renter Plus upsell. It rides the deck with the exact same physics
// as a listing card (draggable, fly-out), but the deck NEVER records a swipe
// for it — dismissing it costs nothing (see the __quota branch in
// swipe_deck.jsx). Content is honest: the number comes from the server quota
// mirrored in the parent and decremented per real swipe.
export default function QuotaCard({ item, onSwipe, active = true, mirrorX, onExited, enterFrom = 0, edge = false, ref }) {
  const navigate = useNavigate();
  const { triggerSwipe, runPreview, style, dragProps } = useSwipePhysics({
    onSwipe: (action) => onSwipe(action, item),
    onExited,
    enabled: active,
    mirrorX,
    enterFrom,
  });

  useImperativeHandle(ref, () => ({ swipe: triggerSwipe, preview: runPreview }), [triggerSwipe, runPreview]);

  const remaining = item.remaining ?? 0;

  function goUnlimited(e) {
    e.stopPropagation();
    navigate("/checkout/renter_plus_monthly", {
      state: {
        item: {
          name: "SwipRent Renter Plus monthly",
          price: 9.99,
          desc: "Unlimited swipes, unlimited rewinds and priority visibility.",
          recurring: "monthly",
        },
        checkout: { product: "renter_plus_monthly" },
      },
    });
  }

  return (
    <motion.div
      className={`absolute inset-0 touch-none select-none overflow-hidden bg-night-surface shadow-ambient ${
        edge ? "rounded-none lg:rounded-3xl" : "rounded-3xl"
      } ${active ? "cursor-grab active:cursor-grabbing" : ""}`}
      style={style}
      {...dragProps}
    >
      <div className="flex h-full w-full flex-col items-center justify-center gap-5 px-8 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-primary/20 text-brand-primary">
          <Flame size={30} strokeWidth={1.8} />
        </span>
        <div>
          <p className="text-3xl font-extrabold tracking-tight text-night-text">
            {remaining} swipe{remaining === 1 ? "" : "s"} left today
          </p>
          <p className="mt-2 text-sm leading-relaxed text-night-sub">
            Free accounts get {item.limit || 20} swipes a day and they reset at midnight.
            Renter Plus removes the cap.
          </p>
        </div>
        <div className="w-full max-w-xs space-y-2.5">
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={goUnlimited}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-brand-primary px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-primary-hover"
          >
            Go unlimited $9.99/mo <ArrowRight size={15} />
          </button>
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); triggerSwipe("pass"); }}
            className="w-full rounded-full px-4 py-3 text-sm font-semibold text-night-sub transition hover:bg-night-tint hover:text-night-text"
          >
            Keep swiping
          </button>
        </div>
        {/* This card never counts against the daily allowance. */}
        <p className="text-[11px] font-medium text-night-sub">Swiping this card away is free.</p>
      </div>
    </motion.div>
  );
}
